<template>
  <div class="accounts__page" :class="{ 'accounts__page--standalone': isStandalone }" v-if="data">
    <Redirect class="" :title="backLabel" :route="backRoute" />

    <div class="page-header-wrap">
      <h2 class="page-title">Your profile</h2>
      <p class="page-subtitle">Manage your personal information, notifications and password</p>
    </div>

    <Notification v-if="errors && errors.length > 0" class="mb-4" :messages="errors" type="error" />

    <!-- Profile header card: avatar + name -->
    <div class="profile-card profile-card--hero">
      <div class="hero-avatar">
        <UploadOneImage :hiddenDelete="true" :image="{ src: data.profilePicture }" @callback="getImageUpLoad" />
      </div>
      <div class="hero-meta">
        <div class="hero-name">
          {{ (data.firstName || '') + ' ' + (data.lastName || '') || 'Your name' }}
        </div>
        <div class="hero-domain">
          <v-icon size="14" color="#6b7280">mdi-storefront-outline</v-icon>
          <span>{{ domain }}</span>
        </div>
      </div>
    </div>

    <!-- Personal information -->
    <div class="profile-card">
      <div class="card-head">
        <div class="card-head-icon">
          <v-icon size="18" color="#2563eb">mdi-account-outline</v-icon>
        </div>
        <div>
          <h3 class="card-title">Personal information</h3>
          <p class="card-desc">Your basic profile details shown across the workspace.</p>
        </div>
      </div>
      <div class="card-body">
        <v-row dense>
          <v-col cols="12" sm="6">
            <InputDefault :model="['data', 'firstName']" fieldName="First name" label="First name" :isValidate="true" />
          </v-col>
          <v-col cols="12" sm="6">
            <InputDefault :model="['data', 'lastName']" fieldName="Last name" label="Last Name" :isValidate="true" />
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- Telegram notifications -->
    <div class="profile-card">
      <div class="card-head">
        <div class="card-head-icon card-head-icon--telegram">
          <v-icon size="18" color="#0088cc">mdi-send</v-icon>
        </div>
        <div>
          <h3 class="card-title">Telegram notifications</h3>
          <p class="card-desc">Add <b>@dropnoxe_bot</b> to your Telegram group, then paste the group ID below.</p>
        </div>
      </div>
      <div class="card-body">
        <label class="field-label">Telegram Group ID</label>
        <v-text-field
          v-model="data.telegramGroupIds"
          placeholder="Id1, Id2, ..."
          :error-messages="telegramErrors"
          @blur="validateTelegramGroupIds"
          @input="validateTelegramGroupIds"
          hide-details="auto"
          outlined
          dense
        ></v-text-field>
        <p class="field-hint">Separate multiple group IDs with a comma.</p>
      </div>
    </div>

    <!-- Password reset -->
    <div class="profile-card">
      <div class="card-head">
        <div class="card-head-icon card-head-icon--lock">
          <v-icon size="18" color="#9333ea">mdi-lock-outline</v-icon>
        </div>
        <div>
          <h3 class="card-title">Password</h3>
          <p class="card-desc">Change the password you use to log in to {{ domain }}.</p>
        </div>
      </div>
      <div class="card-body">
        <v-btn v-if="!showFormChangePass" small outlined color="primary" @click="showFormChangePass = true">
          <v-icon left size="16">mdi-key-outline</v-icon>
          Change password
        </v-btn>
        <v-row v-else dense>
          <v-col cols="12" sm="6">
            <label class="field-label">New password</label>
            <v-text-field
              type="password"
              outlined
              dense
              hide-details="auto"
              placeholder="At least 6 characters"
              :error-messages="errorNewPassword"
              v-model="formChangePass.newPassword"
              @input="errorNewPassword = []"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <label class="field-label">Confirm password</label>
            <v-text-field
              type="password"
              outlined
              dense
              hide-details="auto"
              placeholder="Repeat new password"
              :error-messages="errorNoSame"
              v-model="formChangePass.confirmPassword"
              @input="errorNoSame = []"
            />
          </v-col>
          <v-col cols="12">
            <v-btn small text @click="cancelChangePassword">
              <v-icon left size="16">mdi-close</v-icon>
              Cancel password change
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- Sticky action bar -->
    <div v-if="isStoreOwner" class="action-bar">
      <v-spacer></v-spacer>
      <v-btn class="mr-3" :disabled="isSaving" @click="onCancel" depressed>Cancel</v-btn>
      <v-btn color="primary" :loading="isSaving" @click="checkBeforeSubmit" depressed>
        <v-icon left size="16">mdi-content-save-outline</v-icon>
        Save changes
      </v-btn>
    </div>
    <modal model="showModalConfirmPassword" :title="`Please confirm your password`" :callback="submitConfirmPassword">
      <template slot="content">
        <v-col cols="12" sm="12">
          <small class="input-description fz-14"> Enter your current password to continue. </small>
          <v-text-field
            :type="'password'"
            :error-messages="errorPass"
            v-model="confirmPassword"
            @input="errorPass = []"
            fieldName="Confirm Password"
          />
        </v-col>
      </template>
    </modal>
    <modal model="showModalLogout" :title="`Log out ${data.email}`" :callback="submitLogout">
      <template slot="content">
        <small class="input-description"> Are you sure you want to log out? </small>
      </template>
    </modal>
  </div>
</template>
<script>
import Redirect from '@/components/RedirectTo';
import { mapGetters, mapMutations } from 'vuex';
import { required, minLength } from 'vuelidate/lib/validators';
import { authService } from '@/apis/permission';
import imageHelpers from '@/helpers/image';
import { imageFake } from '@/const/image';

const TELEGRAM_GROUP_IDS_REGEX = /^-?\d+(\s*,\s*-?\d+)*$/;

export default {
  components: {
    Redirect,
  },
  data() {
    return {
      domain: window.localStorage.getItem('r_domain'),
      imageHelpers,
      formChangePass: {
        confirmPassword: '',
        newPassword: '',
      },
      showModalConfirmPassword: false,
      showModalLogout: false,
      showFormChangePass: false,
      loadingLogout: false,
      isSaving: false,
      idAccount: null,
      errors: [],
      isStoreOwner: true,
      historyLogin: [],
      confirmPassword: '',
      errorPass: [],
      errorNoSame: [],
      errorNewPassword: [],
      telegramErrors: [],
      data: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        profilePicture: '',
        telegramGroupIds: '',
      },
    };
  },
  validations: {
    data: {
      firstName: { required },
      lastName: { required },
    },
    formChangePass: {
      newPassword: { required, minLength: minLength(6) },
      confirmPassword: { required },
    },
  },
  computed: {
    ...mapGetters(['user']),
    isStandalone() {
      return this.$route.name === 'account-profile';
    },
    backRoute() {
      return this.isStandalone ? 'home' : 'website.settings.account';
    },
    backLabel() {
      return this.isStandalone ? 'STORES' : 'ACCOUNT';
    },
  },
  methods: {
    ...mapMutations(['setUser']),
    getImageUpLoad(images) {
      this.data.profilePicture = images && images.src ? images.src : images;
    },
    onCancel() {
      this.$router.push({ name: this.backRoute });
    },
    cancelChangePassword() {
      this.showFormChangePass = false;
      this.formChangePass.newPassword = '';
      this.formChangePass.confirmPassword = '';
      this.errorNoSame = [];
      this.errorNewPassword = [];
    },
    validateTelegramGroupIds() {
      const value = (this.data.telegramGroupIds || '').trim();
      if (!value) {
        this.telegramErrors = [];
        return true;
      }
      if (!TELEGRAM_GROUP_IDS_REGEX.test(value)) {
        this.telegramErrors = ['Invalid format. Use comma-separated group IDs, e.g. -1001,-1002'];
        return false;
      }
      this.telegramErrors = [];
      return true;
    },
    validateProfileFields() {
      this.$v.data.$touch();
      if (this.$v.data.$invalid) {
        this.errors = ['Please fill in all required fields correctly before saving.'];
        return false;
      }
      this.errors = [];
      return true;
    },
    validatePasswordForm() {
      this.errorNewPassword = [];
      this.errorNoSame = [];

      if (!this.formChangePass.newPassword) {
        this.errorNewPassword = ['New password is required'];
      } else if (this.formChangePass.newPassword.length < 6) {
        this.errorNewPassword = ['New password must be at least 6 characters'];
      }

      if (!this.formChangePass.confirmPassword) {
        this.errorNoSame = ['Please confirm your new password'];
      } else if (this.formChangePass.newPassword !== this.formChangePass.confirmPassword) {
        this.errorNoSame = ['Confirm password does not match'];
      }

      return this.errorNewPassword.length === 0 && this.errorNoSame.length === 0;
    },
    async submitLogout() {
      this.showModalLogout = false;
      this.loadingLogout = true;
      setTimeout(() => {
        localStorage.clear();
        location.reload();
      }, 500);
    },
    async submitConfirmPassword() {
      if (!this.confirmPassword) {
        this.errorPass = ['Current password is required'];
        return;
      }
      this.errorPass = [];
      this.isSaving = true;
      try {
        if (this.showFormChangePass) {
          await authService.changePasswordUser({
            ...this.formChangePass,
            currentPassword: this.confirmPassword,
          });
        }
        await this.onSubmit(this.buildPayload({ confirmPassword: this.confirmPassword }));
      } catch (error) {
        this.errorPass = [error?.response?.data?.message || error?.message || 'Failed to confirm password'];
      } finally {
        this.isSaving = false;
      }
    },
    buildPayload(extra = {}) {
      return {
        email: this.data.email,
        firstName: this.data.firstName,
        lastName: this.data.lastName,
        phone: this.data.phone,
        profilePicture: this.data.profilePicture || imageFake?.user || '',
        telegramGroupIds: (this.data.telegramGroupIds || '').trim(),
        ...extra,
      };
    },
    async checkBeforeSubmit() {
      const profileOk = this.validateProfileFields();
      const telegramOk = this.validateTelegramGroupIds();
      if (!profileOk || !telegramOk) return;

      if (this.showFormChangePass && !this.validatePasswordForm()) return;

      if (this.showFormChangePass) {
        this.confirmPassword = '';
        this.errorPass = [];
        this.showModalConfirmPassword = true;
        return;
      }

      this.isSaving = true;
      try {
        await this.onSubmit(this.buildPayload());
      } finally {
        this.isSaving = false;
      }
    },
    async onSubmit(payload) {
      if (this.$v.data.$invalid) return;
      this.errors = [];
      try {
        const res = await authService.updateUserProfile(payload);
        this.setUser({
          ...res.data,
          // isStoreOwner: this.user.isStoreOwner,
          // isStoreAdmin: this.user.isStoreAdmin,
          // scopes: this.user.scopes,
        });
        localStorage.setItem('dropnoxe-user', JSON.stringify(res.data));
        this.showModalConfirmPassword = false;
        if (this.showFormChangePass) this.cancelChangePassword();
        this.$store.commit('setMessages', {
          messages: 'Profile updated successfully',
          type: 'success',
        });
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'Failed to update profile';
        this.errors = Array.isArray(message) ? message : [message];
      }
    },
    async checkOwnerStore() {
      try {
        const res = await authService.storePermission();
        this.isStoreOwner = res.data.isStoreOwner;
      } catch (error) {
        console.log(error);
      }
    },
  },
  async mounted() {
    const stored = JSON.parse(localStorage.getItem('dropnoxe-user')) || {};
    this.data = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      profilePicture: '',
      telegramGroupIds: '',
      ...stored,
    };
    if (!this.data.profilePicture) {
      this.data.profilePicture = imageFake?.user || '';
    }
    if (this.data && this.data.id) {
      try {
        this.idAccount = this.data.id;
        const history = await authService.apiHistoryAccountById(this.idAccount);
        this.historyLogin = history.data;
      } catch (error) {
        console.log(error);
      }
    }
  },
};
</script>
<style lang="scss" scoped>
.accounts__page {
  .page-header-wrap {
    padding: 4px 0 16px;
  }
  .page-title {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
    margin: 0 0 4px;
    color: #111827;
    letter-spacing: -0.2px;
  }
  .page-subtitle {
    margin: 0;
    color: #6b7280;
    font-size: 13px;
  }

  .profile-card {
    background: #fff;
    border: 1px solid #eef0f3;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 6px 16px rgba(15, 23, 42, 0.04);
    padding: 20px 22px;
    margin-bottom: 16px;
  }

  .profile-card--hero {
    display: flex;
    align-items: center;
    gap: 18px;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
    border: 1px solid #e5e7eb;
  }

  .hero-avatar {
    flex: 0 0 auto;
    width: 110px;
    ::v-deep .upload-image-wrap,
    ::v-deep .v-image,
    ::v-deep img {
      border-radius: 50% !important;
    }
  }
  .hero-meta {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }
  .hero-name {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
    line-height: 1.2;
    word-break: break-word;
  }
  .hero-domain {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #6b7280;
    font-size: 13px;
  }

  .card-head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 14px;
  }
  .card-head-icon {
    flex: 0 0 auto;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #eff6ff;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &--telegram {
      background: #e6f4fb;
    }
    &--lock {
      background: #f5edff;
    }
  }
  .card-title {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 2px;
    color: #111827;
    letter-spacing: 0.1px;
  }
  .card-desc {
    margin: 0;
    color: #6b7280;
    font-size: 12.5px;
    line-height: 1.45;
    b {
      color: #111827;
    }
  }
  .card-body {
    padding-top: 4px;
  }

  .field-label {
    display: block;
    font-size: 12.5px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
  }
  .field-hint {
    margin: 6px 0 0;
    color: #9ca3af;
    font-size: 12px;
  }

  .action-bar {
    position: sticky;
    bottom: 0;
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(8px);
    border: 1px solid #eef0f3;
    border-radius: 14px;
    padding: 12px 16px;
    margin-top: 8px;
    box-shadow: 0 -2px 12px rgba(15, 23, 42, 0.04);
  }

  ::v-deep .v-btn:not(.v-btn--text):not(.v-btn--outlined) {
    text-transform: none;
    letter-spacing: 0.2px;
    font-weight: 500;
    border-radius: 8px;
  }
  ::v-deep .v-btn--outlined,
  ::v-deep .v-btn--text {
    text-transform: none;
    letter-spacing: 0.2px;
    border-radius: 8px;
  }
  ::v-deep .v-text-field--outlined fieldset {
    border-color: #e5e7eb;
  }
  ::v-deep .v-text-field--outlined:hover fieldset {
    border-color: #cbd5e1;
  }
}

.accounts__page--standalone {
  max-width: 920px;
  margin: 0 auto;
  padding: 20px 16px 40px;
  min-height: 100vh;
}

@media (max-width: 600px) {
  .accounts__page {
    .profile-card {
      padding: 16px;
      border-radius: 12px;
    }
    .profile-card--hero {
      flex-direction: column;
      align-items: flex-start;
      text-align: left;
    }
    .hero-avatar {
      width: 88px;
    }
    .action-bar {
      padding: 10px 12px;
    }
  }
  .accounts__page--standalone {
    padding: 12px 10px 28px;
  }
}

.h-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.h-table td,
.h-table th {
  border-bottom: 1px solid #ddd;
  padding: 12px;
}

.h-table th {
  padding-top: 2px;
  padding-bottom: 2px;
  text-align: left;
  color: black;
  p {
    margin-bottom: 0px !important;
  }
}
</style>
